import React, { useState, useEffect } from 'react';
import { Search, Mail, Send, Clock, UserCheck, Trash2 } from 'lucide-react';
import { Card, Button, Badge } from '../ui';
import { invitationService, Invitation, InvitationListParams } from '../../services/invitationService';

const PendingInvitations: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvitations, setTotalInvitations] = useState(0);
  const limit = 20;

  const loadInvitations = async (params: InvitationListParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await invitationService.getInvitations({
        page: currentPage,
        limit,
        status: 'pending', // Only show pending invitations
        email: searchTerm || undefined,
        ...params,
      });

      if (response.success) {
        setInvitations(response.data.invitations);
        setTotalInvitations(response.data.pagination.total);
        setTotalPages(Math.ceil(response.data.pagination.total / limit));
      } else {
        throw new Error(response.error?.message || 'Failed to load invitations');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadInvitations();
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    try {
      setResendingIds(prev => new Set(prev).add(invitation.id));
      
      // Use the id from the invitation for the API call (backend updated to use id as token)
      const response = await invitationService.resendInvitation(invitation.id);
      
      if (response.success) {
        // Optionally refresh the list to update timestamps
        loadInvitations();
      } else {
        throw new Error(response.error?.message || 'Failed to resend invitation');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invitation');
    } finally {
      setResendingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitation.id);
        return newSet;
      });
    }
  };

  const handleCancelInvitation = async (invitation: Invitation) => {
    if (!window.confirm(`Remove invitation for ${invitation.email}?`)) return;
    try {
      setCancellingIds(prev => new Set(prev).add(invitation.id));
      const response = await invitationService.cancelInvitation(invitation.id);
      if (response.success) {
        loadInvitations();
      } else {
        throw new Error(response.error?.message || 'Failed to remove invitation');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove invitation');
    } finally {
      setCancellingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitation.id);
        return newSet;
      });
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const hoursUntilExpiry = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilExpiry < 24 && hoursUntilExpiry > 0;
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  if (loading && invitations.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Pending Invitations</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading invitations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Pending Invitations</h2>
          <p className="text-sm text-gray-600">
            Manage pending user invitations
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent"
            />
          </div>
        </div>
        
        <Button variant="outline" onClick={handleSearch}>
          Search
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Invitations List */}
      <div className="space-y-4">
        {invitations.map((invitation) => (
          <Card key={invitation.id} variant="outlined" className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {invitation.email}
                    </h3>
                    <Badge variant="warning">
                      Pending
                    </Badge>
                    {isExpired(invitation.expiresAt) && (
                      <Badge variant="error">
                        Expired
                      </Badge>
                    )}
                    {isExpiringSoon(invitation.expiresAt) && !isExpired(invitation.expiresAt) && (
                      <Badge variant="warning">
                        <Clock className="h-3 w-3 mr-1" />
                        Expiring Soon
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</span>
                      <p className="text-sm text-gray-900 mt-1">{invitation.organizationName}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Role</span>
                      <p className="text-sm text-gray-900 mt-1">{invitation.roleName}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Invited By</span>
                      <p className="text-sm text-gray-900 mt-1">
                        {invitation.inviter.firstName} {invitation.inviter.lastName}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</span>
                      <p className="text-sm text-gray-900 mt-1">{formatDateTime(invitation.expiresAt)}</p>
                    </div>
                  </div>
                  <div className="mt-3 text-sm">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</span>
                    <p className="text-sm text-gray-900 mt-1">{formatDateTime(invitation.createdAt)}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleResendInvitation(invitation)}
                  disabled={resendingIds.has(invitation.id) || cancellingIds.has(invitation.id)}
                  title="Resend Invitation"
                >
                  {resendingIds.has(invitation.id) ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-3 w-3 mr-1" />
                      Resend
                    </>
                  )}
                </Button>
                {isExpired(invitation.expiresAt) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCancelInvitation(invitation)}
                    disabled={cancellingIds.has(invitation.id) || resendingIds.has(invitation.id)}
                    title="Remove Invitation"
                    className="text-red-600 hover:text-red-700 hover:border-red-300"
                  >
                    {cancellingIds.has(invitation.id) ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalInvitations)} of {totalInvitations} invitations
          </p>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && invitations.length === 0 && (
        <div className="text-center py-12">
          <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No pending invitations</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm ? 
              'No invitations match your search criteria.' : 
              'All invitations have been accepted or have expired.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default PendingInvitations;